/**
 * Phase 45 Plan 45-08 — task attachment API client (D-16).
 *
 * Backend contract (45-04):
 *   GET    /api/tasks/:taskId/attachments              → TaskAttachmentDto[]
 *   POST   /api/tasks/:taskId/attachments              multipart/form-data, field "file"
 *   DELETE /api/tasks/:taskId/attachments/:attachmentId → 204
 *
 * Per 45-04 SUMMARY Deviation #1: upload uses server-side multipart
 * (NOT presigned-PUT) so we send the file as multipart/form-data; the backend
 * streams it to S3 via fileUploadService.uploadFile and the response carries
 * a fresh 1-hour signed download URL ready for inline rendering.
 */
import { API_BASE_URL, ApiClientError, getBaseAuthHeaders, apiGet, apiDelete } from './client';

export interface TaskAttachmentDto {
  id: number;
  taskId: number;
  s3Key: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: number | null;
  createdAt: string;
  uploader: {
    id: number;
    firstName: string | null;
    lastName: string | null;
  } | null;
  downloadUrl: string;
}

export async function fetchTaskAttachments(taskId: number): Promise<TaskAttachmentDto[]> {
  const response = await apiGet<{ data: TaskAttachmentDto[] }>(
    `/tasks/${taskId}/attachments`,
  );
  return response.data;
}

export async function uploadTaskAttachment(
  taskId: number,
  file: File,
): Promise<TaskAttachmentDto> {
  const formData = new FormData();
  formData.append('file', file);
  // Browser sets Content-Type with multipart boundary; we deliberately omit it.
  const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/attachments`, {
    method: 'POST',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      error: 'Upload Failed',
      message: response.statusText,
    }));
    throw new ApiClientError(error);
  }
  const payload = (await response.json()) as { data: TaskAttachmentDto };
  return payload.data;
}

export async function deleteTaskAttachment(
  taskId: number,
  attachmentId: number,
): Promise<void> {
  await apiDelete<void>(`/tasks/${taskId}/attachments/${attachmentId}`);
}

/**
 * Quick task 260810-lpq (LPQ-02) — mint a FRESH signed download URL for a
 * single task attachment on demand, instead of relying on the `downloadUrl`
 * field baked into the list response at fetch time (which can go stale if a
 * slide-over is left open longer than the server's 1-hour TTL).
 *
 * Pass `disposition: 'attachment'` for the explicit download control
 * (LPQ-01) — forces a save regardless of content type.
 */
export async function mintTaskAttachmentDownloadUrl(
  taskId: number,
  attachmentId: number,
  disposition?: 'attachment',
): Promise<{ url: string; expiresAt: string }> {
  const query = disposition ? `?disposition=${disposition}` : '';
  const response = await apiGet<{ data: { url: string; expiresAt: string } }>(
    `/tasks/${taskId}/attachments/${attachmentId}/download-url${query}`,
  );
  return response.data;
}
